import { CreatePipelineStepPaths } from '../create-pipeline/state-machine/step-paths';

export interface JobMode {
  name?: string;
  desc?: string;
  value: string;
  historicalMode?: boolean;
  settingsPath?: string;
  showHistoricalModeWarning?: boolean;
  canMergeTables?: boolean;
  canProvideMultipleDbNames?: boolean;
  multipleDbSelectionDisabled?: boolean;
  canShowRedoLogAdvancedSettings?: boolean;
  hideSchemaName?: boolean;
  isRecommended?: boolean;
  icon?: string;
  showAdvancedSettings?: boolean;
  showSetupGuide?: boolean;
  canSkipNewTables?: boolean;
  virtualValue?: string;
}

export class JobModeEnum {
  static TABLE: JobMode = {
    name: 'Table',
    value: 'table',
    desc: 'Ingests data by running SQL queries on your Source table. This mode captures changes using modified or updated timestamp columns. Deletes are not replicated to the Destination',
    icon: 'table',
    showAdvancedSettings: false,
    showSetupGuide: true,
    canSkipNewTables: true,
    historicalMode: false
  };
  static QUERY: JobMode = {
    name: 'Custom SQL',
    value: 'query',
    desc: 'Ingests data by running a custom SQL query that you provide on the Source database. This mode allows you to replicate database views or specific datasets. Deletes are not replicated to the Destination.',
    settingsPath: CreatePipelineStepPaths.CUSTOM_SQL_SETTINGS,
    icon: 'custom-sql',
    showAdvancedSettings: false,
    showSetupGuide: true
  };
  static COLLECTION: JobMode = {
    name: 'Collections',
    desc: 'Hevo polls all of your collections individually at a fixed frequency. ' +
      'Use this mode when you are looking to fetch data from multiple collections in your database.',
    value: 'collection'
  };
  static OPLOG: JobMode = {
    name: 'OpLog',
    desc: 'Ingests data through Change Data Capture (CDC) using MongoDB’s OpLog. This mode captures every insert, update, and delete operation from the Source database in near real time.',
    value: 'oplog',
    historicalMode: true,
    canMergeTables: true,
    canProvideMultipleDbNames: true,
    showAdvancedSettings: true,
    showSetupGuide: true,
    canSkipNewTables: true,
  };
  static CHANGE_STREAMS: JobMode = {
    name: 'Change Streams',
    desc: 'Ingests data through Change Data Capture (CDC) using MongoDB Change Streams, available from version 4.0 onward. This mode captures real-time insert, update, and delete events directly from the Source database.',
    value: 'change_streams',
    historicalMode: true,
    canMergeTables: true,
    canProvideMultipleDbNames: true,
    showAdvancedSettings: true,
    multipleDbSelectionDisabled: true,
    showSetupGuide: true,
    canSkipNewTables: true,
    isRecommended: true
  };
  static BINLOG: JobMode = {
    name: 'BinLog',
    desc: 'Ingests data using MySQL Binary Logs (BinLog). This is the most efficient and reliable method to capture real-time data changes, and it replicates inserts, updates, and deletes to the Destination.',
    value: 'wal',
    historicalMode: true,
    canMergeTables: true,
    canProvideMultipleDbNames: true,
    isRecommended: true,
    icon: 'binlog',
    showAdvancedSettings: true,
    showSetupGuide: true,
    canSkipNewTables: true
  };

  static BINLOG_AURORA: JobMode = {
    name: 'BinLog',
    desc: 'Ingests data using Aurora’s Binary Logs (BinLog). This is the most efficient and reliable method to capture real-time data changes, and it replicates inserts, updates, and deletes to the Destination.',
    value: 'wal',
    historicalMode: true,
    canMergeTables: true,
    canProvideMultipleDbNames: true,
    isRecommended: true,
    icon: 'binlog',
    showAdvancedSettings: true,
    showSetupGuide: true,
    canSkipNewTables: true
  };
  static LOGIC_REPL: JobMode = {
    name: 'Logical Replication',
    value: 'wal',
    desc: 'Ingests data using PostgreSQL Write-Ahead Logs (WALs). This is the most efficient and reliable method to capture real-time data changes, and it replicates inserts, updates, and deletes to the Destination.',
    historicalMode: true,
    canMergeTables: true,
    hideSchemaName: true,
    isRecommended: true,
    showAdvancedSettings: true,
    icon: 'extract',
    canSkipNewTables: true
  };

  static XMIN: JobMode = {
    name: 'XMIN',
    value: 'xmin',
    desc: 'Incrementally reads insert and updates via the XMIN system column. ' +
      'We cannot replicate deletes via this mode.',
    icon: 'xmin',
    historicalMode: true,
    canMergeTables: true,
    hideSchemaName: true,
    showAdvancedSettings: true,
    canSkipNewTables: true
  };

  static REDO_LOG: JobMode = {
    name: 'RedoLog',
    value: 'wal',
    desc: 'Ingests data using Oracle’s LogMiner utility to read Redo Logs. This is the most efficient and reliable method to capture real-time data changes, and it replicates inserts, updates, and deletes to the Destination.',
    historicalMode: true,
    canMergeTables: true,
    hideSchemaName: true,
    canShowRedoLogAdvancedSettings: true,
    isRecommended: true,
    showAdvancedSettings: true,
    icon: 'extract',
    canSkipNewTables: true
  };
  static CHANGE_TRACKING: JobMode = {
    name: 'Change Tracking',
    value: 'table',
    virtualValue: 'change_tracking',
    desc: 'Ingests data using SQL Server’s Change Tracking feature. This mode captures inserts, updates, and deletes from Source tables when Change Tracking is enabled on both the database and the tables. Enable Change Tracking before setting up the Pipeline.',
    icon: 'change-stream',
    showAdvancedSettings: false,
    showSetupGuide: true,
    canSkipNewTables: true,
    isRecommended: true,
    historicalMode: false
  };
  static APPSFLYER: JobMode = {
    value: 'appsflyer'
  };
  static FBA: JobMode = {
    value: 'fba'
  };
  static ELASTICSEARCH: JobMode = {
    value: 'es',
    canSkipNewTables: true,
    historicalMode: true
  };
  static DYNAMODB: JobMode = {
    value: 'dynamodb',
    canSkipNewTables: true
  };
  static SALESFORCE: JobMode = {
    value: 'salesforce',
    canSkipNewTables: true
  };
}

